import Styles from './GoalSelection.module.scss'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { Text } from 'blaze-ui/Text/Text'
import { useEffect, useState } from 'react'
import { Pressable } from '@react-aria/interactions'
import cx from 'classnames'
import { Pill } from 'components/Pill/Pill'
import type { BusinessType, Goal } from 'types'
import { BUSINESS_TYPE_LABELS, GOAL_OPTIONS_BY_BUSINESS_TYPE } from './utils'
import { Heading } from 'blaze-ui/Heading/Heading'
import { strategiesDefaultData, Strategy } from '../StrategiesSelection/utils'
import { TopBar } from '../TopBar/TopBar'
import { Button } from 'blaze-ui/Button'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

export interface GoalSelectionProps {
  onNext: (goal: Goal, brandPlanStrategies: Strategy[]) => void
  onBack: () => void
  selectedBusinessType?: BusinessType
  initialSelectedGoal?: Goal
}

export const GoalSelection = ({
  onNext,
  onBack,
  selectedBusinessType = 'service',
  initialSelectedGoal,
}: GoalSelectionProps) => {
  const { isMobile } = useResponsiveDesign()
  const goalOptions = selectedBusinessType ? GOAL_OPTIONS_BY_BUSINESS_TYPE[selectedBusinessType] : []
  const [selectedGoal, setSelectedGoal] = useState<Goal | undefined>(
    initialSelectedGoal || goalOptions.find((g) => g.isRecommended)?.id,
  )

  useEffect(() => {
    if (selectedBusinessType && selectedGoal) {
      const options = GOAL_OPTIONS_BY_BUSINESS_TYPE[selectedBusinessType] || []
      const isValidForBusinessType = options.some((g) => g.id === selectedGoal)
      if (!isValidForBusinessType) {
        setSelectedGoal(undefined)
      }
    }
  }, [selectedBusinessType, selectedGoal])
  const businessTypeLabel = selectedBusinessType ? BUSINESS_TYPE_LABELS[selectedBusinessType] : ''

  const handleNext = () => {
    if (selectedBusinessType && selectedGoal) {
      onNext(selectedGoal, strategiesDefaultData[selectedBusinessType].strategies)
    }
  }

  const isNextDisabled = !selectedBusinessType || !selectedGoal

  return (
    <MultiStepStepLayout title="What's your content goal?" scrollContainerClassName={Styles.scrollContainer} headingLevel={isMobile ? 2 : 1}>
      <StickyFooterProvider
        footer={
          <StickyFooter>
            <FooterButtons>
              {!isMobile && (
                <Button variant="tertiary" size="lg" onPress={onBack}>
                  Back
                </Button>
              )}
              <Button size="lg" onPress={handleNext} isDisabled={isNextDisabled} fullWidth={isMobile}>
                Next
              </Button>
            </FooterButtons>
          </StickyFooter>
        }
      >
        <div className={Styles.sectionsContainer}>
          <TopBar highlightStep="goalsAndPlan" />
          {selectedBusinessType && goalOptions.length > 0 && (
            <div className={Styles.section} role="group" aria-label="Goal selection">
              <Heading level={4}>
                Choose the goal for your content plan based on what's best for your {businessTypeLabel} business
              </Heading>
              <div className={Styles.goalsContainer}>
                {goalOptions.map((goal, index) => {
                  const isSelected = selectedGoal ? selectedGoal === goal.id : goal.isRecommended

                  return (
                    <Pressable key={goal.id} onPress={() => setSelectedGoal(goal.id)}>
                      <div
                        className={cx(Styles.goalCard, { [Styles.goalCardSelected]: isSelected })}
                        data-testid={`goal-option-${goal.id}`}
                      >
                        {goal.isRecommended && (
                          <Pill
                            size="sm"
                            className={Styles.recommendedPill}
                            style={{
                              backgroundColor: goal.backgroundColor,
                              color: goal.iconColor,
                            }}
                          >
                            Recommended
                          </Pill>
                        )}
                        {!isMobile && (
                          <div className={Styles.goalIconContainer} style={{ backgroundColor: goal.backgroundColor }}>
                            <goal.icon size={28} color={goal.iconColor} />
                          </div>
                        )}
                        <div
                          className={cx(Styles.goalContentText, {
                            [Styles.largeText]: selectedBusinessType === 'place' && [2, 3].includes(index),
                          })}
                        >
                          <Heading level={isMobile ? 5 : 2}>{goal.title}</Heading>
                          <Text variant="secondary" color="var(--dark-60)" className={Styles.goalDescription}>
                            {goal.description}
                          </Text>
                        </div>
                      </div>
                    </Pressable>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </StickyFooterProvider>
    </MultiStepStepLayout>
  )
}
